import { useEffect } from 'react';
import { InputLabel } from '@/components/ui/InputLabel';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { Webinar } from '@/features/webinar/types';
import { getFontFamily } from '@/lib/utils';
import { Clock } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { listPresenters } from '@/features/webinar/webinarThunks';
import { useTranslation } from 'react-i18next';
import { toWebinarLng } from '@/lib/webinarLanguage';

export default function PreviewAttendeeSignin({
  visualIdentity,
  theme,
  accentColor,
  backgroundColor,
  contrastColor,
  maxWidth,
  scale = 0.35,
  containerClassName,
  backgroundImage,
  displayLogo = false,
  displaySpeakers = false,
  firstNameField = true,
  lastNameField = false,
  emailField = true,
  phoneField = false,
  firstNameMandatory = true,
  lastNameMandatory = false,
  emailMandatory = true,
  phoneMandatory = false,
  submitButtonLabel,
  webinar,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  theme: 'LIGHT' | 'DARK';
  accentColor: string;
  backgroundColor: string;
  contrastColor: string;
  maxWidth?: string | number | null;
  scale?: number;
  containerClassName?: string;
  backgroundImage?: string;
  displayLogo?: boolean;
  displaySpeakers?: boolean;
  firstNameField?: boolean;
  lastNameField?: boolean;
  emailField?: boolean;
  phoneField?: boolean;
  firstNameMandatory?: boolean;
  lastNameMandatory?: boolean;
  emailMandatory?: boolean;
  phoneMandatory?: boolean;
  submitButtonLabel?: string;
  webinar?: Webinar | null;
}) {
  const { t } = useTranslation('webinars', {
    lng: webinar ? toWebinarLng(webinar.language) : undefined,
  });
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const presenters = useAppSelector((state: RootState) => state.webinar.presenters);

  useEffect(() => {
    if (displaySpeakers && webinar?.id && workspaceId) {
      dispatch(listPresenters({ workspaceId, webinarId: webinar.id }));
    }
  }, [displaySpeakers, webinar?.id, workspaceId, dispatch]);

  const getMaxWidth = () => {
    if (maxWidth === null) return undefined;
    if (maxWidth === undefined) return '100%';
    return typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
  };

  const getMaxHeight = () => {
    if (maxWidth === null) return undefined;
    if (maxWidth === undefined) return '432px';
    return undefined;
  };

  const isGradient = backgroundColor && backgroundColor.includes('linear-gradient');

  const getBackgroundStyle = () => {
    if (backgroundImage) {
      return {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }
    if (isGradient) {
      return { background: backgroundColor };
    }
    return { backgroundColor: backgroundColor };
  };

  return (
    <div
      className={`p-4 w-full rounded-[20px] relative flex items-center justify-center overflow-hidden ${containerClassName || ''}`}
      style={{
        ...getBackgroundStyle(),
        maxWidth: getMaxWidth(),
        maxHeight: getMaxHeight(),
      }}
    >
      {/* logo welya */}
      <div className="absolute top-0 right-0 p-4 flex items-center gap-2 scale-[1] ">
        <p
          className="text-white font-medium text-[8px] "
          style={{
            fontFamily: getFontFamily(visualIdentity?.fontFamily),
            color: theme === 'LIGHT' ? '#000000' : '#FFFFFF',
          }}
        >
          powered by
        </p>
        {theme === 'LIGHT' && (
          <img src="/assets/logo-black.svg" alt="logo" width={50} height={50} />
        )}
        {theme === 'DARK' && <img src="/assets/logo-white.svg" alt="logo" width={50} height={50} />}
      </div>
      <div style={{ transform: `scale(${scale})` }}>
        <div
          className={`p-8 my-16 rounded-[20px] space-y-8 flex flex-col items-center text-center ${maxWidth === null ? 'w-full' : 'max-w-175'}`}
          style={{
            backgroundColor:
              theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
          }}
        >
          {visualIdentity?.logoUrl && displayLogo && (
            <img
              src={visualIdentity?.logoUrl}
              alt="logo"
              width={140}
              height={64}
              className="object-contain"
            />
          )}

          <div className="flex flex-col gap-6 items-center justify-center">
            <h1
              className={`text-2xl font-bold ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              {webinar?.title || 'Lorem ipsum dolor sit amet consectetur.'}
            </h1>

            {webinar?.shortDescription && (
              <h2
                className={`text-xl font-bold ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                }}
              >
                {webinar.shortDescription}
              </h2>
            )}

            {webinar?.description && (
              <p
                className={`text-sm font-normal ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                }}
              >
                {webinar.description}
              </p>
            )}

            {displaySpeakers && presenters.length > 0 && (
              <div className="flex flex-col gap-4">
                {presenters.map((presenter) => {
                  const displayName = presenter.overrideFullName || presenter.displayName;
                  const pictureUrl = presenter.userPictureUrl || '/assets/avatar.svg';

                  return (
                    <div key={presenter.id} className="flex items-center gap-4">
                      <div className="size-12">
                        <img
                          src={pictureUrl}
                          alt={displayName}
                          width={50}
                          height={50}
                          className="rounded-full object-cover"
                        />
                      </div>
                      <div>
                        <p
                          className={theme === 'DARK' ? 'text-white' : 'text-black'}
                          style={{
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                          }}
                        >
                          {t('publicSignin.presenterBy')}
                          <span
                            className=" text-xl font-semibold"
                            style={{
                              fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            }}
                          >
                            {displayName}
                          </span>
                          {/* {presenter.jobTitle && (
                            <>
                              {' '}
                              <span
                                className="text-sm font-normal"
                                style={{
                                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                                }}
                              >
                                - {presenter.jobTitle}
                              </span>
                            </>
                          )} */}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div
              className="border p-4 rounded-2xl w-full flex items-center justify-center"
              style={{
                borderColor: contrastColor,

                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <Clock className="size-6 text-[#9E9FA6] mr-2" />
              <div
                className={`text-base font-semibold mr-2 ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                }}
              >
                10:00 Europe/Paris
              </div>
            </div>
          </div>

          <div className="w-full">
            <p
              className={`text-sm pb-4 text-left ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              {t('publicSignin.formTitle')}
            </p>

            <div className="flex flex-col gap-6 pb-8">
              {firstNameField && (
                <InputLabel
                  label={`${t('publicSignin.firstName')}${firstNameMandatory ? '*' : ''}`}
                  id="name"
                  type="text"
                  theme={theme}
                  fontFamily={getFontFamily(visualIdentity?.fontFamily)}
                />
              )}

              {lastNameField && (
                <InputLabel
                  label={`${t('publicSignin.lastName')}${lastNameMandatory ? '*' : ''}`}
                  id="lastname"
                  type="text"
                  theme={theme}
                  fontFamily={getFontFamily(visualIdentity?.fontFamily)}
                />
              )}

              {emailField && (
                <InputLabel
                  label={`${t('publicSignin.email')}${emailMandatory ? '*' : ''}`}
                  id="email"
                  type="email"
                  theme={theme}
                  fontFamily={getFontFamily(visualIdentity?.fontFamily)}
                />
              )}

              {phoneField && (
                <InputLabel
                  label={`${t('publicSignin.phone')}${phoneMandatory ? '*' : ''}`}
                  id="phone"
                  type="tel"
                  theme={theme}
                  fontFamily={getFontFamily(visualIdentity?.fontFamily)}
                />
              )}

              <div
                className="hover:opacity-90 mx-auto min-h-10 min-w-[248px] max-w-full w-fit px-6 py-3 cursor-pointer rounded-full flex items-center justify-center text-center whitespace-normal wrap-break-word transition-opacity"
                style={{
                  backgroundColor: accentColor,
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                }}
              >
                {submitButtonLabel || t('publicSignin.register')}
              </div>
            </div>
          </div>

          <p
            className={theme === 'DARK' ? 'text-white' : 'text-black'}
            style={{
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('publicSignin.disclaimer')}
          </p>

          {/* <div className="flex justify-center items-center gap-8">
            <p
              className="cursor-pointer text-sm font-semibold"
              style={{
                color: contrastColor,
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              Termes et conditions
            </p>
            <p
              className="cursor-pointer text-sm font-semibold"
              style={{
                color: contrastColor,
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              Politique de confidentialité
            </p>
            <p
              className="cursor-pointer text-sm font-semibold"
              style={{
                color: contrastColor,
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              DMCA
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
}
